import { useState, useRef, useEffect } from 'react';

const MultiSelect = ({ options = [], value = [], onChange, disabled = false, placeholder = 'Select values...' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) =>
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);

  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  const triggerLabel =
    value.length === 0 ? placeholder :
    value.length === 1 ? value[0] :
    `${value.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '4px', padding: '6px 10px',
          background: 'var(--surface, #fff)',
          border: '1px solid var(--border, #d0d5dd)',
          borderRadius: '6px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '13px',
          color: disabled ? 'var(--muted, #9aa3b7)' : 'var(--text, #1a1f36)',
          opacity: disabled ? 0.6 : 1,
          textAlign: 'left',
          height: '36px',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {triggerLabel}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {value.length > 0 && (
            <span
              onClick={clearAll}
              title="Clear selection"
              style={{ fontSize: '11px', color: 'var(--muted)', cursor: 'pointer', lineHeight: 1, padding: '0 2px' }}
            >✕</span>
          )}
          <span style={{ fontSize: '9px', color: 'var(--muted)' }}>{open ? '▲' : '▼'}</span>
        </span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, zIndex: 1000,
          minWidth: '100%', maxHeight: '220px', overflowY: 'auto',
          background: 'var(--surface, #fff)',
          border: '1px solid var(--border, #d0d5dd)',
          borderRadius: '6px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
        }}>
          {options.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--muted)' }}>No values</div>
          ) : options.map((opt) => {
            const checked = value.includes(opt);
            return (
              <label
                key={opt}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '4px 10px', cursor: 'pointer', fontSize: '12px',
                  color: 'var(--text)', userSelect: 'none',
                  background: checked ? 'rgba(33,150,243,0.07)' : 'transparent',
                  borderBottom: '1px solid var(--border-faint, rgba(0,0,0,0.05))',
                }}
                onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = checked ? 'rgba(33,150,243,0.07)' : 'transparent'; }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(opt)}
                  style={{ margin: 0, cursor: 'pointer', accentColor: '#2196f3', width: '12px', height: '12px' }}
                />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
